import { all, call, delay, fork, put, takeEvery, takeLatest } from "redux-saga/effects";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import {
  createAudit as createAuditApi,
  getAuditJob,
  listAudits,
  requestAuditReport as requestAuditReportApi,
  unwrapGet,
  unwrapPost,
} from "../../helpers/auditHub_helper";
import {
  createAudit,
  createAuditFailure,
  createAuditSuccess,
  fetchAudits,
  fetchAuditsFailure,
  fetchAuditsSuccess,
  requestAuditReport,
  requestAuditReportFailure,
  requestAuditReportSuccess,
} from "./auditHubSlice";

const failPayload = (error, fallback) => ({
  message: error?.message || getApiErrorMessage(error?.data || error) || fallback,
});

function* fetchAuditsWorker({ payload }) {
  try {
    const response = yield call(listAudits, payload || {});
    const body = unwrapGet(response);
    yield put(
      fetchAuditsSuccess({
        items: body.items || [],
        page: body.page,
        size: body.size,
        total: body.total,
      })
    );
  } catch (error) {
    yield put(fetchAuditsFailure(failPayload(error, "Unable to load audits.")));
  }
}

function* createAuditWorker({ payload }) {
  try {
    const response = yield call(createAuditApi, payload);
    const body = unwrapPost(response);
    yield put(createAuditSuccess(body));
  } catch (error) {
    yield put(createAuditFailure(failPayload(error, "Unable to create the audit.")));
  }
}

function* requestAuditReportWorker({ payload }) {
  const auditId = payload?.auditId;
  const format = payload?.format || "docx";
  if (!auditId) {
    yield put(requestAuditReportFailure({ message: "Missing audit id." }));
    return;
  }
  try {
    const created = unwrapPost(yield call(requestAuditReportApi, auditId, { format }));
    let job = created;
    for (let i = 0; i < 15; i += 1) {
      if (job?.status === "READY" && job?.downloadUrl) break;
      if (job?.status === "FAILED") {
        throw new Error(job.error || "Report generation failed.");
      }
      yield delay(1500);
      job = unwrapGet(yield call(getAuditJob, auditId, job.jobId));
    }
    if (job?.status === "READY" && job?.downloadUrl) {
      yield put(requestAuditReportSuccess(job));
    } else {
      yield put(
        requestAuditReportFailure({
          message: "Report is still generating. Try again in a moment.",
        })
      );
    }
  } catch (error) {
    yield put(requestAuditReportFailure(failPayload(error, "Unable to generate the report.")));
  }
}

function* watchFetchAudits() {
  yield takeLatest(fetchAudits.type, fetchAuditsWorker);
}
function* watchCreateAudit() {
  yield takeEvery(createAudit.type, createAuditWorker);
}
function* watchRequestReport() {
  yield takeLatest(requestAuditReport.type, requestAuditReportWorker);
}

export default function* auditHubSaga() {
  yield all([fork(watchFetchAudits), fork(watchCreateAudit), fork(watchRequestReport)]);
}
